<!-- src/components/NavBar.vue -->
<template>
  <header class="navbar" :class="{ 'menu-open': menuOpen }">
    <div class="navbar-inner">
      <router-link :to="homeRoute" class="nav-logo" @click="closeMenu">
        <img :src="require('@/assets/IMG_7005.png')" alt="Logo BullVerge" class="logo-image" />
        <span class="logo-word">BullVerge</span>
      </router-link>

      <nav class="nav-links desktop-only" :aria-label="$t('nav.mainLabel')">
        <router-link
          class="nav-link"
          :class="{ active: isActive('/', true) }"
          :to="homeRoute"
          @mouseenter="closeDesktopDropdowns"
          @click="closeDesktopDropdowns"
        >
          {{ $t('nav.links.home') }}
        </router-link>

        <div
          v-for="group in navGroups"
          :key="group.id"
          class="nav-group"
          :class="{ open: openGroupId === group.id }"
          @mouseenter="openDesktopGroup(group.id)"
          @mouseleave="scheduleCloseDesktopGroup(group.id)"
          @focusin="openDesktopGroup(group.id)"
          @focusout="handleDesktopGroupFocusOut(group.id, $event)"
        >
          <router-link
            class="nav-link nav-group-trigger"
            :class="{ active: isActive(group.path) }"
            :to="localizedRoute(group.path)"
            aria-haspopup="true"
            :aria-expanded="(openGroupId === group.id).toString()"
            @click="closeDesktopDropdowns"
          >
            <span>{{ group.label }}</span>
            <span class="nav-caret" aria-hidden="true">⌄</span>
          </router-link>
          <div class="nav-dropdown">
            <router-link
              v-for="item in group.items"
              :key="item.path"
              class="nav-dropdown-link"
              :class="{ active: isActive(item.path, item.exact) }"
              :to="localizedRoute(item.path)"
              @click="closeDesktopDropdowns"
            >
              <span>{{ item.label }}</span>
              <small>{{ item.description }}</small>
            </router-link>
          </div>
        </div>

        <router-link
          class="nav-link"
          :class="{ active: isActive('/aegis', true) }"
          :to="localizedRoute('/aegis')"
          @mouseenter="closeDesktopDropdowns"
          @click="closeDesktopDropdowns"
        >
          {{ $t('nav.links.aegis') }}
        </router-link>

        <router-link
          class="nav-link"
          :class="{ active: isActive('/contact', true) }"
          :to="localizedRoute('/contact')"
          @mouseenter="closeDesktopDropdowns"
          @click="closeDesktopDropdowns"
        >
          {{ $t('nav.links.contacts') }}
        </router-link>
      </nav>

      <div class="nav-actions desktop-only">
        <div class="language-switcher" role="group" :aria-label="$t('language.select')">
          <button
            v-for="option in languageOptions"
            :key="option.code"
            type="button"
            class="language-option"
            :class="{ active: currentLocale === option.code }"
            :aria-pressed="currentLocale === option.code"
            :title="option.label"
            @click="selectLocale(option.code)"
          >
            <span aria-hidden="true">{{ option.flag }}</span>
            <span>{{ option.shortLabel }}</span>
          </button>
        </div>
        <a
          href="https://instagram.com/bullverge_universe"
          target="_blank"
          rel="noopener"
          class="badge-link"
        >
          <span aria-hidden="true">＠</span>
          <span class="label">{{ $t('nav.social') }}</span>
        </a>
        <a
          href="https://gofund.me/a3d9ca91"
          target="_blank"
          rel="noopener"
          class="cta-link"
        >
          <span class="dot"></span>
          <span>{{ $t('nav.supportProject') }}</span>
        </a>
      </div>

      <button
        class="nav-toggle"
        type="button"
        @click="toggleMenu"
        :aria-expanded="menuOpen.toString()"
        aria-controls="mobile-menu"
      >
        <span class="toggle-line"></span>
        <span class="toggle-line"></span>
        <span class="toggle-line"></span>
        <span class="sr-only">{{ $t('nav.openMenu') }}</span>
      </button>
    </div>

    <transition name="drawer">
      <div
        v-if="menuOpen"
        id="mobile-menu"
        class="mobile-drawer"
        :aria-label="$t('nav.mobileLabel')"
      >
        <nav class="mobile-links">
          <router-link
            :to="homeRoute"
            class="mobile-link"
            :class="{ active: isActive('/', true) }"
            @click="closeMenu"
          >
            {{ $t('nav.links.home') }}
          </router-link>

          <section v-for="group in navGroups" :key="`mobile-${group.id}`" class="mobile-group">
            <router-link
              class="mobile-group-title"
              :to="localizedRoute(group.path)"
              :class="{ active: isActive(group.path) }"
              @click="closeMenu"
            >
              <span>{{ group.label }}</span>
              <small>{{ group.description }}</small>
            </router-link>
            <router-link
              v-for="item in group.items"
              :key="`mobile-${item.path}`"
              :to="localizedRoute(item.path)"
              class="mobile-link nested"
              :class="{ active: isActive(item.path, item.exact) }"
              @click="closeMenu"
            >
              {{ item.label }}
            </router-link>
          </section>

          <router-link
            :to="localizedRoute('/aegis')"
            class="mobile-link"
            :class="{ active: isActive('/aegis', true) }"
            @click="closeMenu"
          >
            {{ $t('nav.links.aegis') }}
          </router-link>
          <router-link
            :to="localizedRoute('/contact')"
            class="mobile-link"
            :class="{ active: isActive('/contact', true) }"
            @click="closeMenu"
          >
            {{ $t('nav.links.contacts') }}
          </router-link>
        </nav>
        <div class="mobile-actions">
          <div class="language-switcher mobile-language" role="group" :aria-label="$t('language.select')">
            <button
              v-for="option in languageOptions"
              :key="`mobile-${option.code}`"
              type="button"
              class="language-option"
              :class="{ active: currentLocale === option.code }"
              :aria-pressed="currentLocale === option.code"
              @click="selectLocale(option.code)"
            >
              <span aria-hidden="true">{{ option.flag }}</span>
              <span>{{ option.label }}</span>
            </button>
          </div>
          <a
            href="https://instagram.com/bullverge_universe"
            target="_blank"
            rel="noopener"
            class="mobile-pill"
            @click="closeMenu"
          >{{ $t('nav.social') }}</a>
          <a
            href="https://gofund.me/a3d9ca91"
            target="_blank"
            rel="noopener"
            class="mobile-pill emphasis"
            @click="closeMenu"
          >{{ $t('nav.supportBullVerge') }}</a>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { LOCALE_OPTIONS, setLocale } from '@/i18n'

function splitTarget(target) {
  const [path, hash] = String(target || '/').split('#')
  return {
    path: path || '/',
    hash: hash ? `#${hash}` : ''
  }
}

export default {
  name: 'NavBar',
  data() {
    return {
      menuOpen: false,
      openGroupId: null,
      dropdownCloseTimer: null
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    languageOptions() {
      return LOCALE_OPTIONS.map((option) => ({
        ...option,
        label: option.code === 'it' ? this.$t('language.italian') : this.$t('language.english')
      }))
    },
    homeRoute() {
      return this.localizedRoute('/')
    },
    navGroups() {
      return [
        {
          id: 'ecosystem',
          path: '/ecosystem',
          label: this.$t('nav.links.ecosystem'),
          description: this.$t('nav.descriptions.ecosystem'),
          items: [
            {
              path: '/ecosystem',
              label: this.$t('nav.links.ecosystem'),
              description: this.$t('nav.descriptions.ecosystem'),
              exact: true
            },
            {
              path: '/ecosystem/products',
              label: this.$t('nav.links.products'),
              description: this.$t('nav.descriptions.products')
            },
            {
              path: '/ecosystem/products/prometeo',
              label: this.$t('nav.links.prometeo'),
              description: this.$t('nav.descriptions.prometeo'),
              exact: true
            },
            {
              path: '/ecosystem/products/nemo',
              label: this.$t('nav.links.nemo'),
              description: this.$t('nav.descriptions.nemo'),
              exact: true
            },
            {
              path: '/ecosystem/products/backsense',
              label: this.$t('nav.links.backsense'),
              description: this.$t('nav.descriptions.backsense'),
              exact: true
            },
            {
              path: '/ecosystem/products/backsense/aura',
              label: this.$t('nav.links.aura'),
              description: this.$t('nav.descriptions.aura'),
              exact: true
            },
            {
              path: '/ecosystem/modules',
              label: this.$t('nav.links.modules'),
              description: this.$t('nav.descriptions.modules'),
              exact: true
            }
          ]
        },
        {
          id: 'academy',
          path: '/academy',
          label: this.$t('nav.links.academy'),
          description: this.$t('nav.descriptions.academy'),
          items: [
            {
              path: '/academy',
              label: this.$t('nav.links.academy'),
              description: this.$t('nav.descriptions.academy'),
              exact: true
            },
            {
              path: '/academy/papers',
              label: this.$t('nav.links.papers'),
              description: this.$t('nav.descriptions.papers')
            },
            {
              path: '/academy#research-notes',
              label: this.$t('nav.links.researchNotes'),
              description: this.$t('nav.descriptions.researchNotes'),
              exact: true
            },
            {
              path: '/academy/faq',
              label: this.$t('nav.links.faq'),
              description: this.$t('nav.descriptions.faq'),
              exact: true
            }
          ]
        }
      ]
    }
  },
  mounted() {
    this.$router.isReady().then(() => this.syncLocaleRoute())
  },
  beforeUnmount() {
    this.clearDropdownCloseTimer()
  },
  watch: {
    currentLocale() {
      this.syncLocaleRoute()
    },
    '$route.fullPath'() {
      this.syncLocaleRoute()
      this.closeDesktopDropdowns()
    }
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen
    },
    closeMenu() {
      this.menuOpen = false
      this.closeDesktopDropdowns()
    },
    clearDropdownCloseTimer() {
      if (!this.dropdownCloseTimer) return
      clearTimeout(this.dropdownCloseTimer)
      this.dropdownCloseTimer = null
    },
    openDesktopGroup(groupId) {
      this.clearDropdownCloseTimer()
      this.openGroupId = groupId
    },
    scheduleCloseDesktopGroup(groupId) {
      this.clearDropdownCloseTimer()
      this.dropdownCloseTimer = setTimeout(() => {
        if (this.openGroupId === groupId) this.openGroupId = null
        this.dropdownCloseTimer = null
      }, 180)
    },
    closeDesktopDropdowns() {
      this.clearDropdownCloseTimer()
      this.openGroupId = null
    },
    handleDesktopGroupFocusOut(groupId, event) {
      const nextTarget = event.relatedTarget
      if (nextTarget && event.currentTarget.contains(nextTarget)) return
      this.scheduleCloseDesktopGroup(groupId)
    },
    localizedRoute(target) {
      const parsed = splitTarget(target)
      const route = { path: parsed.path }
      if (parsed.hash) route.hash = parsed.hash
      if (this.currentLocale === 'en') route.query = { lang: 'en' }
      return route
    },
    queryForLocale(locale) {
      const query = { ...this.$route.query }

      if (locale === 'en') {
        query.lang = 'en'
      } else {
        delete query.lang
      }

      return query
    },
    syncLocaleRoute() {
      const query = this.queryForLocale(this.currentLocale)
      const currentLang = this.$route.query.lang
      const nextLang = query.lang

      if (currentLang === nextLang || (!currentLang && !nextLang)) return

      this.$router.replace({ path: this.$route.path, query, hash: this.$route.hash }).catch(() => {})
    },
    isActive(target, exact = false) {
      const { path, hash } = splitTarget(target)
      const currentPath = this.$route.path

      if (path === '/') return currentPath === '/'
      if (hash && this.$route.hash !== hash) return false
      if (exact) return currentPath === path
      return currentPath === path || currentPath.startsWith(`${path}/`)
    },
    selectLocale(locale) {
      const nextLocale = String(locale || '').toLowerCase().slice(0, 2) === 'en' ? 'en' : 'it'
      const query = this.queryForLocale(nextLocale)

      setLocale(nextLocale)
      this.$router.replace({ path: this.$route.path, query, hash: this.$route.hash }).catch(() => {})
      this.closeMenu()
    }
  }
}
</script>

<style scoped>
.navbar {
  position: sticky;
  top: 0;
  z-index: 999;
  padding: 0.72rem clamp(1rem, 3vw, 2rem);
  background: linear-gradient(150deg, rgba(7, 16, 34, 0.92), rgba(12, 23, 42, 0.88));
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(126, 176, 222, 0.28);
}

.navbar.menu-open {
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 18px;
}

.navbar-inner {
  display: flex;
  align-items: center;
  gap: clamp(0.8rem, 2vw, 1.5rem);
}

.nav-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.72rem;
  flex-shrink: 0;
  padding: 0.28rem 0.7rem 0.28rem 0.3rem;
  border-radius: 8px;
  border: 1px solid rgba(140, 193, 241, 0.26);
  background: linear-gradient(130deg, rgba(17, 32, 58, 0.72), rgba(10, 22, 43, 0.62));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 32px rgba(3, 8, 22, 0.35);
  text-decoration: none;
  color: #f2f6ff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.logo-image {
  width: 44px;
  height: 44px;
  padding: 0.28rem;
  border-radius: 8px;
  background: radial-gradient(circle at 30% 30%, rgba(255, 195, 134, 0.28), rgba(124, 206, 255, 0.18) 70%, transparent 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  object-fit: contain;
  filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.35));
}

.logo-word {
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.nav-group {
  position: relative;
}

.nav-group::after {
  position: absolute;
  left: 0;
  top: 100%;
  width: min(360px, calc(100vw - 2rem));
  height: 0.9rem;
  content: '';
}

.nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-height: 40px;
  padding: 0.5rem 0.78rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: rgba(233, 236, 249, 0.82);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.22s ease, background 0.22s ease;
}

.nav-link:hover,
.nav-link.active,
.nav-group.open .nav-group-trigger {
  color: #f7fdff;
  background: rgba(121, 222, 255, 0.11);
}

.nav-caret {
  color: var(--accent-amber);
  font-size: 0.95rem;
}

.nav-dropdown {
  position: absolute;
  left: 0;
  top: calc(100% + 0.65rem);
  display: grid;
  width: min(360px, calc(100vw - 2rem));
  padding: 0.55rem;
  border-radius: 8px;
  border: 1px solid rgba(127, 210, 255, 0.24);
  background: rgba(7, 18, 32, 0.96);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.42);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-6px);
  transition:
    opacity 0.18s ease 0.12s,
    transform 0.18s ease 0.12s,
    visibility 0s linear 0.3s;
  visibility: hidden;
}

.nav-group.open .nav-dropdown {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
  transition-delay: 0s;
  visibility: visible;
}

.nav-dropdown-link {
  display: grid;
  gap: 0.18rem;
  padding: 0.72rem 0.8rem;
  border-radius: 8px;
  color: var(--text-main);
  text-decoration: none;
}

.nav-dropdown-link:hover,
.nav-dropdown-link.active {
  background: rgba(255, 255, 255, 0.065);
}

.nav-dropdown-link small {
  color: var(--text-soft);
  line-height: 1.35;
}

.nav-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.language-switcher {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.22rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 202, 248, 0.24);
  background: rgba(255, 255, 255, 0.06);
}

.language-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-width: 48px;
  min-height: 32px;
  padding: 0.3rem 0.48rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(231, 237, 250, 0.76);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
}

.language-option.active {
  color: #091426;
  background: linear-gradient(120deg, #ffbe80, #82d9ff);
  box-shadow: 0 8px 22px rgba(130, 217, 255, 0.25);
}

.language-option:focus-visible {
  outline: 2px solid rgba(130, 217, 255, 0.7);
  outline-offset: 2px;
}

.badge-link {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 201, 145, 0.9);
  text-decoration: none;
  border: 1px solid rgba(255, 190, 129, 0.48);
  background: rgba(255, 176, 108, 0.14);
}

.badge-link .label {
  font-weight: 600;
}

.cta-link {
  display: inline-flex;
  align-items: center;
  gap: 0.48rem;
  min-height: 38px;
  padding: 0.52rem 0.9rem;
  border-radius: 8px;
  text-decoration: none;
  color: #111d33;
  font-weight: 700;
  background: linear-gradient(120deg, #ffbe80, #82d9ff);
  box-shadow: 0 16px 34px rgba(130, 217, 255, 0.28);
}

.cta-link .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #111d33;
}

.nav-toggle {
  display: none;
  margin-left: auto;
  width: 46px;
  height: 46px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  padding: 0;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.toggle-line {
  display: block;
  width: 18px;
  height: 2px;
  background: #f5f6ff;
  border-radius: 2px;
  transform-origin: center;
  transition: transform 0.3s ease, opacity 0.3s ease, width 0.3s ease;
}

.menu-open .nav-toggle .toggle-line:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.menu-open .nav-toggle .toggle-line:nth-child(2) {
  opacity: 0;
  transform: scaleX(0.3);
}

.menu-open .nav-toggle .toggle-line:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.mobile-drawer {
  display: none;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.desktop-only {
  display: flex;
}

@media (max-width: 1180px) {
  .badge-link,
  .cta-link {
    display: none;
  }
}

@media (max-width: 980px) {
  .desktop-only {
    display: none;
  }

  .nav-toggle {
    display: inline-flex;
  }

  .navbar-inner {
    gap: 1rem;
  }

  .mobile-drawer {
    display: block;
    margin-top: 0.8rem;
    padding: 1rem;
    background: linear-gradient(160deg, rgba(16, 24, 52, 0.95), rgba(7, 10, 26, 0.95));
    border: 1px solid rgba(134, 189, 255, 0.18);
    border-radius: 8px;
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.45);
  }

  .mobile-links {
    display: grid;
    gap: 0.7rem;
  }

  .mobile-group {
    display: grid;
    gap: 0.5rem;
    padding: 0.65rem;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.045);
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  .mobile-group-title {
    display: grid;
    gap: 0.15rem;
    color: var(--text-main);
    text-decoration: none;
    font-weight: 700;
  }

  .mobile-group-title small {
    color: var(--text-soft);
    font-weight: 400;
  }

  .mobile-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.82rem 0.92rem;
    border-radius: 8px;
    text-decoration: none;
    color: rgba(232, 236, 249, 0.9);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid transparent;
  }

  .mobile-link.nested {
    padding-block: 0.68rem;
    background: rgba(4, 13, 24, 0.45);
  }

  .mobile-link.active,
  .mobile-group-title.active {
    border-color: rgba(114, 249, 242, 0.4);
    color: #f7fdff;
  }

  .mobile-actions {
    margin-top: 1rem;
    display: grid;
    gap: 0.75rem;
  }

  .mobile-language {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-radius: 8px;
  }

  .mobile-language .language-option {
    width: 100%;
    min-height: 42px;
    border-radius: 8px;
  }

  .mobile-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.82rem 1rem;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;
    color: rgba(228, 233, 247, 0.92);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
  }

  .mobile-pill.emphasis {
    background: linear-gradient(120deg, #ffbe80, #82d9ff);
    color: #111d33;
    border-color: transparent;
  }
}

@media (max-width: 420px) {
  .logo-word {
    display: none;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
